import React, { useState, useEffect, useCallback, useMemo } from 'react';
import DataGrid, { Column, MasterDetail, Editing, RequiredRule, PatternRule, Selection } from 'devextreme-react/data-grid';
import DropDownBox from 'devextreme-react/drop-down-box';
import { TextBox } from 'devextreme-react';
import { InitNewRowEvent, RowInsertingEvent, RowRemovingEvent, RowUpdatingEvent } from 'devextreme/ui/data_grid';

import { formatMessage } from 'devextreme/localization';

import MetaApiPathTemplate from './MetaApiPathTemplate';

import MetaApiApi from '../../api/MetaApiApi';
import CustomerApiCredentialApi from '../../api/CustomerApiCredentialApi';

import CustomerFragmentDto from '../../classes/dtos/metaapimodule/CustomerFragmentDto';
import ApiFragmentDto from '../../classes/dtos/metaapimodule/ApiFragmentDto';
import CustomerApiCredentialListDto from '../../classes/dtos/credentialmodule/CustomerApiCredentialListDto';

import './metaapis.scss';

function GridInDropDownCell({ cellInfo, customerApiCredentialListDto }: { cellInfo: any; customerApiCredentialListDto: CustomerApiCredentialListDto[] | null }) {
    const [selectedKeys, setSelectedKeys] = useState(cellInfo.value || []);
    const [isOpened, setIsOpened] = useState(false);

    const onSelectionChanged = (e: any) => {
        setSelectedKeys(e.selectedRowKeys);
        cellInfo.setValue(e.selectedRowKeys);
    };

    return (

        <DropDownBox
            value={selectedKeys}
            opened={isOpened}
            valueExpr="Id"
            placeholder={formatMessage("MetaApisGrid_SelectACredential")}
            showClearButton={false}
            dataSource={customerApiCredentialListDto}
            onValueChanged={(e) => {
                setSelectedKeys(e.value || []);
                cellInfo.setValue(e.value || []);
            }}
            onOpenedChange={(opened) => setIsOpened(opened)}
            fieldRender={() => (
                <TextBox>
                    <div style={{ marginLeft: '16px', flexDirection: 'row', display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: 2, lineHeight: '20px', cursor: 'pointer' }}>
                        {(customerApiCredentialListDto || [])
                            .filter(cac => (cellInfo.value || []).includes(cac.Id))
                            .map((cac, idx, arr) => (
                                <span key={cac.Id} style={{ background: '#b33d3d', borderRadius: '14px', paddingLeft: '16px', paddingRight: '16px', marginRight: idx < arr.length - 1 ? '4px' : '0', display: 'inline-block', whiteSpace: 'nowrap', height: '20px' }}>
                                    {cac.Name}
                                </span>
                            ))
                        }
                        {(!cellInfo.value || cellInfo.value.length === 0) && (
                            <span>
                                {formatMessage("MetaApisGrid_NoCredentials")}
                            </span>
                        )}
                    </div>
                </TextBox>
            )}
            contentRender={() => (
                <DataGrid
                    dataSource={customerApiCredentialListDto}
                    selectedRowKeys={selectedKeys}
                    onSelectionChanged={onSelectionChanged}
                    hoverStateEnabled={true}
                    height={300}
                    keyExpr="Id"
                    showBorders={true}
                >
                    <Selection mode="multiple" showCheckBoxesMode="always" />
                    <Column dataField="Name" caption="Name" minWidth={200} />
                    <Column dataField="TypeLabel" caption="Type" width={200} />
                    <Column dataField="ValidityStart" caption="Validity start" dataType="date" width={140} />
                    <Column dataField="ValidityEnd" caption="Validity end" dataType="date" width={140} />
                </DataGrid>
            )}
        />
    );
}

export default function MetaApis() {

    const [customerFragmentDto, setCustomerFragmentDto] = useState<CustomerFragmentDto | null>(null);
    const [customerApiCredentialListDto, setCustomerApiCredentialListDto] = useState<CustomerApiCredentialListDto[] | null>(null);

    useEffect(() => {
        if (customerFragmentDto === null) {
            MetaApiApi.GetMetaApiSettingsAsync()
                .then(detail => {
                    setCustomerFragmentDto(detail.Result);
                });
        }
    }, [customerFragmentDto]);

    useEffect(() => {
        if (customerApiCredentialListDto === null) {
            CustomerApiCredentialApi.GetCustomerApiCredentialsAsync()
                .then(list => {
                    setCustomerApiCredentialListDto(list.Result);
                });
        }
    }, [customerApiCredentialListDto]);

    // La grille ne doit recevoir une nouvelle reference de dataSource que lorsque les donnees
    // changent reellement : sinon chaque rendu React reconfigure le widget DevExtreme.
    const gridDataSource = useMemo(() => customerFragmentDto?.ApiFragments ?? [], [customerFragmentDto]);

    const initNewRow = useCallback((e: InitNewRowEvent<ApiFragmentDto, any>) => {
        e.data.SwaggerVisible = true;
    }, []);

    const insertRow = useCallback((e: RowInsertingEvent<ApiFragmentDto, any>) => {

        if (customerFragmentDto === null) {
            e.cancel = true;
            return;
        }

        MetaApiApi.CreateApiFragmentAsync(customerFragmentDto.CustomerFragmentId, e.data.Fragment, e.data.SwaggerVisible, e.data.CustomerApiCredentialIds)
            .then((e) => {
                if (e.IsFailed()) {
                    alert("ERROR");
                }
                setCustomerFragmentDto(null);
            });
    }, [customerFragmentDto]);

    const updateRow = useCallback((e: RowUpdatingEvent<ApiFragmentDto, any>) => {

        if (customerFragmentDto === null) {
            e.cancel = true;
            return;
        }

        let apiFragmentId = e.newData.ApiFragmentId ?? e.oldData.ApiFragmentId;
        let customerFragmentId = customerFragmentDto.CustomerFragmentId;
        let fragment = e.newData.Fragment ?? e.oldData.Fragment;
        let swaggerVisible = e.newData.SwaggerVisible ?? e.oldData.SwaggerVisible;
        let customerApiCredentialIds = e.newData.CustomerApiCredentialIds ?? e.oldData.CustomerApiCredentialIds;

        MetaApiApi.UpdateApiFragmentAsync(apiFragmentId, customerFragmentId, fragment, swaggerVisible, customerApiCredentialIds)
            .then((e) => {
                if (e.IsFailed()) {
                    alert("ERROR");
                }
                setCustomerFragmentDto(null);
            });
    }, [customerFragmentDto]);

    const removeRow = useCallback((e: RowRemovingEvent<ApiFragmentDto, any>) => {

        if (customerFragmentDto === null) {
            e.cancel = true;
            return;
        }

        MetaApiApi.DeleteApiFragmentAsync(e.data.ApiFragmentId)
            .then((e) => {
                if (e.IsFailed()) {
                    alert("ERROR");
                }
                setCustomerFragmentDto(null);
            });
    }, [customerFragmentDto]);

    const refresh = useCallback(() => {
        setCustomerFragmentDto(null);
    }, []);

    // Composant DropDownBox avec DataGrid interne
    const GridInDropDown = useCallback((cellInfo: any) => (
        <GridInDropDownCell cellInfo={cellInfo} customerApiCredentialListDto={customerApiCredentialListDto} />
    ), [customerApiCredentialListDto]);

    const customerApiCredentialTagsCell = useCallback((cellData: any) => {
        const customerApiCredentialIds = cellData.value || [];

        const selectedItems = (customerApiCredentialListDto ?? []).filter(c =>
            customerApiCredentialIds.includes(c.Id)
        );

        return (
            <div style={{ flexDirection: 'row', display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: 2, lineHeight: '20px' }}>
                {selectedItems.length === 0 ? (
                    <span>{formatMessage("MetaApisGrid_NoCredentials")}</span>
                ) : (
                    selectedItems.map((cac, idx, arr) => (
                        <span key={cac.Id} style={{ background: '#b33d3d', borderRadius: '14px', paddingLeft: '16px', paddingRight: '16px', marginRight: idx < arr.length - 1 ? '4px' : '0', display: 'inline-block', whiteSpace: 'nowrap', height:'20px' }}>
                            {cac.Name}
                        </span>
                    ))
                )}
            </div>
        );
    }, [customerApiCredentialListDto]);

    // customerApiCredentialListDto peut encore etre null quand les fragments arrivent en premier :
    // lever une exception ici casserait le rendu interne de la grille.
    const calculateCredentialsDisplayValue = useCallback((rowData: ApiFragmentDto) => {
        if (!rowData.CustomerApiCredentialIds || rowData.CustomerApiCredentialIds.length === 0) {
            return formatMessage("MetaApisGrid_NoCredentials");
        }
        return rowData.CustomerApiCredentialIds
            .map((id: number) => {
                const cac = (customerApiCredentialListDto ?? []).find(c => c.Id === id);
                return cac ? cac.Name : '';
            })
            .filter(Boolean)
            .join(', ');
    }, [customerApiCredentialListDto]);

    const renderSwaggerCell = useCallback(({ data }: any) => (
        data.Url ? (
            <a href={data.Url} target="_blank" rel="noopener noreferrer">
                Go to swagger
            </a>
        ) : null
    ), []);

    const renderMasterDetail = useCallback((detailData: any) => (
        <MetaApiPathTemplate
            key={detailData.key}
            data={detailData}
            refresh={refresh}
        />
    ), [refresh]);

    const editingTexts = useMemo(() => ({ addRow: 'Add a new API' }), []);
    const fragmentPattern = useMemo(() => /^[a-z0-9_-]+$/, []);

    return (
        <div className="metaapis-container">
            <div className={"metaapis-internal-container"}>
                <div className={"metaapis-title-container"}>
                    <h2>{"All your meta APIs"}</h2>
                </div>
                <div>
                    <DataGrid
                        id="grid-container"
                        dataSource={gridDataSource}
                        keyExpr="ApiFragmentId"
                        showBorders={true}
                        onInitNewRow={initNewRow}
                        onRowInserting={insertRow}
                        onRowUpdating={updateRow}
                        onRowRemoving={removeRow}>

                        <Editing
                            mode="row"
                            allowUpdating={true}
                            allowDeleting={true}
                            allowAdding={true}
                            texts={editingTexts} />

                        <Column dataField="Fragment" width={270} caption="Api">
                            <RequiredRule message="This field is required." />
                            <PatternRule pattern={fragmentPattern} message="Only lowercase letters, numbers, hyphens, and underscores are allowed." />
                        </Column>
                        <Column dataField="SwaggerVisible" width={150} dataType="boolean" />
                        <Column dataField="Url" caption="Swagger" allowEditing={false} width='150px' cellRender={renderSwaggerCell} />

                        <Column
                            dataField="CustomerApiCredentialIds"
                            caption={formatMessage("MetaApisGrid_Credentials")}
                            editCellRender={GridInDropDown}
                            cellRender={customerApiCredentialTagsCell}
                            calculateDisplayValue={calculateCredentialsDisplayValue}
                        />

                        <MasterDetail
                            enabled={true}
                            render={renderMasterDetail}
                        />
                    </DataGrid>
                </div>
            </div>
        </div>
    );
}